// src/utils/analytics.ts
import { useEffect, useRef } from 'react';

export enum EventType {
  CODE_COPY = 'code_copy',
  SCROLL_DEPTH = 'scroll_depth',
  DEMO_INTERACTION = 'demo_interaction',
  TIME_ON_PAGE = 'time_on_page',
  BLOG_POST_VIEW = 'blog_post_view'
}

interface EventData {
  page: string;
  postId?: string;
  post_title?: string;
  post_slug?: string;
  language?: string;
  depth?: number;
  timeInSeconds?: number;
  demoName?: string;
  action?: string;
}

/**
 * Track custom dev-focused events
 */
export function trackEvent(eventType: EventType, data: EventData): void {
  if (typeof window === 'undefined') return;
  
  // Add user session ID if available
  const sessionId = localStorage.getItem('blog_session_id') || generateSessionId();
  
  // Format the event data
  const eventData = {
    ...data,
    sessionId,
    timestamp: new Date().toISOString(),
    eventType
  };
  
  if (process.env.NODE_ENV === 'production') {
    sendAnalyticsEvent(eventData);
  } else {
    console.log(`[DEV Analytics] Event: ${eventType}`, eventData);
  }
}

/**
 * Track code copy events
 */
export function useCodeCopyTracking(): void {
  useEffect(() => {
    const trackCodeCopy = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const codeBlock = target.closest('pre');
      
      if (codeBlock) {
        // Check if the click was on a copy button or code block
        if (
          target.classList.contains('copy-button') || 
          target.tagName === 'CODE' ||
          target.closest('.copy-button')
        ) {
          const language = 
            codeBlock.querySelector('code')?.className.match(/language-(\w+)/)?.[1] || 
            'unknown';
            
          trackEvent(EventType.CODE_COPY, {
            page: window.location.pathname,
            language,
            postId: window.location.pathname.split('/').pop() || ''
          });
        }
      }
    };
    
    document.addEventListener('click', trackCodeCopy);
    return () => document.removeEventListener('click', trackCodeCopy);
  }, []);
}

/**
 * Track scroll depth on blog posts
 */
export function useScrollDepthTracking(): void {
  const scrollDepths = useRef<Set<number>>(new Set());
  
  useEffect(() => {
    const handleScroll = () => {
      // Calculate the current scroll position
      const scrollPos = window.scrollY + window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      const scrollPercentage = Math.round((scrollPos / docHeight) * 100);
      
      // Track 25%, 50%, 75%, 100% scroll depths
      [25, 50, 75, 100].forEach(depth => {
        if (scrollPercentage >= depth && !scrollDepths.current.has(depth)) {
          scrollDepths.current.add(depth);
          trackEvent(EventType.SCROLL_DEPTH, {
            page: window.location.pathname,
            depth,
            postId: window.location.pathname.split('/').pop() || ''
          });
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
}

/**
 * Track time spent on page
 */
export function useTimeOnPageTracking(): void {
  const startTime = useRef<number>(Date.now());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const reported = useRef<Set<number>>(new Set());
  
  useEffect(() => {
    // Report time every minute
    intervalRef.current = setInterval(() => {
      const secondsOnPage = Math.floor((Date.now() - startTime.current) / 1000);
      const minutesOnPage = Math.floor(secondsOnPage / 60);
      
      // Only report at 1, 3, 5, 10 minutes
      if ([1, 3, 5, 10].includes(minutesOnPage) && !reported.current.has(minutesOnPage)) {
        reported.current.add(minutesOnPage);
        trackEvent(EventType.TIME_ON_PAGE, {
          page: window.location.pathname,
          timeInSeconds: secondsOnPage,
          postId: window.location.pathname.split('/').pop() || ''
        });
      }
    }, 10000); // Check every 10 seconds
    
    // Clean up
    const startTimeValue = startTime.current;
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Report final time when leaving page
      const secondsOnPage = Math.floor((Date.now() - startTimeValue) / 1000);
      trackEvent(EventType.TIME_ON_PAGE, {
        page: window.location.pathname,
        timeInSeconds: secondsOnPage,
        postId: window.location.pathname.split('/').pop() || '',
        action: 'leave'
      });
    };
  }, []);
}

/**
 * Send analytics data to the server
 * Skip API call in development mode to avoid 404 errors
 */
async function sendAnalyticsEvent(eventData: Record<string, any>) {
  try {
    // Skip actual API calls in development to avoid 404 errors
    if (process.env.NODE_ENV !== 'production') {
      console.log('[DEV Analytics] Event logged:', eventData);
      return { success: true };
    }
    
    const response = await fetch('/api/analytics', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(eventData),
    });

    if (!response.ok) {
      console.error('Analytics error:', response.statusText);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to send analytics event:', error);
    return null;
  }
}

function generateSessionId(): string {
  const randomId = Math.random().toString(36).substring(2, 15);
  localStorage.setItem('blog_session_id', randomId);
  return randomId;
}
